/**
 * CampaignEditDialog — create or edit a campaign.
 *
 * Props:
 *   open        - bool
 *   onClose     - () => void
 *   campaign    - existing campaign object (edit mode) or null (create mode)
 *   onSaved     - (campaign) => void  fires after a successful save chain
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControlLabel, Switch, Box, Typography,
  IconButton, Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api from '../services/api';
import CampaignPoster from './CampaignPoster';


export default function CampaignEditDialog({ open, onClose, campaign, onSaved }) {
  const isEdit = !!campaign?.id;
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [archived, setArchived] = useState(false);
  // Poster state: either the existing file (rendered via CampaignPoster) OR
  // a freshly picked File the user wants to upload. After successful save
  // we POST the file to /poster.
  const [pickedFile, setPickedFile] = useState(null);
  const [pickedPreview, setPickedPreview] = useState(null);
  const [clearPoster, setClearPoster] = useState(false);
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    setPickedFile(null);
    setPickedPreview(null);
    setClearPoster(false);
    if (isEdit) {
      setName(campaign.name || '');
      setSlug(campaign.slug || '');
      setDescription(campaign.description || '');
      setArchived(!!campaign.archived_at);
    } else {
      setName('');
      setSlug('');
      setDescription('');
      setArchived(false);
    }
  }, [open, campaign, isEdit]);

  // Manage the object URL lifetime for the local preview of a newly
  // picked file. Revoke on unmount / re-pick.
  useEffect(() => {
    if (!pickedFile) { setPickedPreview(null); return; }
    const url = URL.createObjectURL(pickedFile);
    setPickedPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pickedFile]);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setPickedFile(f);
      setClearPoster(false);
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      let campaignId;
      let savedRow;
      if (isEdit) {
        const resp = await api.patch(`/api/campaigns/${campaign.id}`, {
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || null,
          archived,
        });
        campaignId = campaign.id;
        savedRow = resp.data;
      } else {
        const resp = await api.post('/api/campaigns', {
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || null,
        });
        campaignId = resp.data.id;
        savedRow = resp.data;
      }

      if (pickedFile) {
        const fd = new FormData();
        fd.append('file', pickedFile);
        await api.post(`/api/campaigns/${campaignId}/poster`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (isEdit && clearPoster) {
        await api.delete(`/api/campaigns/${campaignId}/poster`);
      }

      onSaved?.(savedRow);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save campaign.');
    } finally {
      setSaving(false);
    }
  };

  const showExistingPoster = isEdit && !pickedFile && !clearPoster && !!campaign?.has_poster;

  // Mirror backend _slugify: lowercase, [^a-z0-9]+ → _, trim _, max 120.
  // Empty input collapses to 'campaign' so the user can see the floor case.
  const derivedSlug = (name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 120) || 'campaign');
  const slugPreview = !isEdit && !slug.trim() && name.trim() ? derivedSlug : '';

  return (
    <Dialog open={open} onClose={() => !saving && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit campaign' : 'New campaign'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          {/* Poster picker on the left — 2:3 placeholder, fills with
              either the existing poster (edit mode) or the local
              preview of a freshly picked file. */}
          <Box sx={{ flexShrink: 0 }}>
            <Box sx={{
              width: 100, height: 150, borderRadius: 0.5,
              border: 1, borderColor: 'divider', bgcolor: 'background.default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', position: 'relative',
            }}>
              {pickedPreview ? (
                <Box component="img" src={pickedPreview} alt="Poster preview"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : showExistingPoster ? (
                <CampaignPoster
                  campaignId={campaign.id}
                  hasPoster
                  orientation={campaign.poster_orientation}
                  shortEdge={100}
                />
              ) : (
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>No poster</Typography>
              )}
            </Box>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              <Tooltip title="Choose poster image (JPG / PNG / WebP / GIF). Any size or aspect — the server normalizes on upload.">
                <Button size="small" variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()}>
                  {pickedFile ? 'Change' : (showExistingPoster ? 'Replace' : 'Upload')}
                </Button>
              </Tooltip>
              {(pickedFile || showExistingPoster) && (
                <Tooltip title="Remove poster">
                  <IconButton size="small" onClick={() => {
                    if (pickedFile) { setPickedFile(null); }
                    else { setClearPoster(true); }
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handlePick}
            />
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Come What May"
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={slugPreview || (isEdit ? '' : 'auto-derived from name')}
              helperText={
                slugPreview
                  ? `Auto-derived: ${slugPreview} (a _2 / _3 suffix may be added if it collides)`
                  : 'Lowercase identifier. Auto-derived from the name when blank. Used to match project filenames into the campaign.'
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional internal note for the operator"
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
            />
            {isEdit && (
              <FormControlLabel
                control={<Switch checked={archived} onChange={(_e, v) => setArchived(v)} />}
                label="Archived"
              />
            )}
            {error && (
              <Typography variant="caption" color="error">{error}</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
